/**
 * The time-zone list a picker offers.
 *
 * Lives in the headless package because it is data, not markup: the web `<select>` and a native
 * picker should offer the same zones in the same order. The names come from the runtime's own
 * IANA database (`Intl.supportedValuesOf`) where it exists — that keeps the list current without
 * this package shipping a zone table it would have to maintain — and fall back to a hand-picked
 * set of widely-used zones where it doesn't (older Safari, Hermes without full ICU).
 *
 * Note what this is *not*: a tz database. The package still never converts between zones (ADR
 * 0017); a zone here is a label the event travels with.
 */

/** Enough coverage to be usable when the runtime can't enumerate zones itself. */
export const FALLBACK_TIME_ZONES: readonly string[] = [
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'Africa/Nairobi',
  'America/Anchorage',
  'America/Argentina/Buenos_Aires',
  'America/Bogota',
  'America/Chicago',
  'America/Denver',
  'America/Halifax',
  'America/Lima',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/New_York',
  'America/Phoenix',
  'America/Sao_Paulo',
  'America/Toronto',
  'America/Vancouver',
  'Asia/Bangkok',
  'Asia/Dubai',
  'Asia/Hong_Kong',
  'Asia/Jakarta',
  'Asia/Jerusalem',
  'Asia/Kolkata',
  'Asia/Manila',
  'Asia/Seoul',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Brisbane',
  'Australia/Melbourne',
  'Australia/Perth',
  'Australia/Sydney',
  'Europe/Amsterdam',
  'Europe/Athens',
  'Europe/Berlin',
  'Europe/Brussels',
  'Europe/Dublin',
  'Europe/Istanbul',
  'Europe/Lisbon',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Moscow',
  'Europe/Paris',
  'Europe/Prague',
  'Europe/Rome',
  'Europe/Stockholm',
  'Europe/Warsaw',
  'Europe/Zurich',
  'Pacific/Auckland',
  'Pacific/Fiji',
  'Pacific/Honolulu',
];

/**
 * Exact country metadata for zones whose generic runtime names are abbreviations or cities.
 * Other zones use the runtime's own localized generic timezone name, keeping the web embed small.
 */
const TIME_ZONE_COUNTRY_DATA = `
US:America/Anchorage,America/Chicago,America/Los_Angeles,America/New_York,America/Phoenix,Pacific/Honolulu;
AR:America/Argentina/Buenos_Aires;
CA:America/Halifax,America/Toronto,America/Vancouver;
MX:America/Mexico_City;
BR:America/Sao_Paulo;
ID:Asia/Jakarta;
SG:Asia/Singapore;
AU:Australia/Brisbane,Australia/Melbourne,Australia/Perth,Australia/Sydney;
RU:Europe/Moscow;
FJ:Pacific/Fiji;
CI:Africa/Abidjan;
DE:Europe/Berlin
`.trim();

const TIME_ZONE_COUNTRIES: ReadonlyMap<string, string> = new Map(
  TIME_ZONE_COUNTRY_DATA.split(';').flatMap((entry) => {
    const [countryCode, zones] = entry.trim().split(':');
    if (countryCode === undefined || zones === undefined) return [];
    return zones.split(',').map((zone): [string, string] => [zone, countryCode]);
  }),
);

/**
 * The language a zone's country is named in, when a caller doesn't say.
 *
 * `'en'` rather than the runtime's own locale, deliberately: these labels sit inside a form
 * whose every other word is English, and a German browser reading "Berlin, Deutschland" under an
 * English heading is a worse experience than a consistent one. It is the *UI* language, so a host
 * that translates its page passes its own locale and gets zone names to match.
 */
const DEFAULT_LOCALE = 'en';

/** One `Intl.DisplayNames` per locale: building one is expensive and the picker names ~400 zones. */
const COUNTRY_DISPLAY_NAMES = new Map<string, Intl.DisplayNames | undefined>();

function countryDisplayNames(locale: string): Intl.DisplayNames | undefined {
  if (!COUNTRY_DISPLAY_NAMES.has(locale)) {
    try {
      COUNTRY_DISPLAY_NAMES.set(locale, new Intl.DisplayNames(locale, { type: 'region' }));
    } catch {
      // An unparseable tag, or a runtime without DisplayNames: the code itself is the label.
      COUNTRY_DISPLAY_NAMES.set(locale, undefined);
    }
  }
  return COUNTRY_DISPLAY_NAMES.get(locale);
}

function countryDisplayName(countryCode: string, locale: string): string {
  return countryDisplayNames(locale)?.of(countryCode) ?? countryCode;
}

/**
 * Uses the runtime's generic name for zones outside the compact exact map.
 *
 * English-only, whatever the caller's locale: the heuristic is "the name ends in ` Time`", which
 * is a fact about English and has no equivalent in a locale whose generic name is
 * "Mitteleuropäische Zeit". What comes back is a name, not a region code, so there is nothing to
 * translate afterwards either. Zones in the exact map above — every zone whose runtime name is an
 * abbreviation or a city, which is most of the ones people search for — are localized properly.
 */
function runtimeTimeZoneCountry(zone: string): string | undefined {
  let country: string | undefined;
  try {
    const name = new Intl.DateTimeFormat('en', {
      timeZone: zone,
      timeZoneName: 'shortGeneric',
    })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value;
    if (name !== undefined && name.endsWith(' Time')) {
      const candidate = name.slice(0, -' Time'.length);
      if (normalizedSearch(candidate) !== normalizedSearch(timeZoneLabel(zone))) {
        country = candidate;
      }
    }
  } catch {
    // Invalid zones and runtimes without generic names simply have no country label.
  }
  return country;
}

interface IntlWithZones {
  supportedValuesOf?: (key: string) => string[];
}

/** Every zone this runtime knows, sorted; the fallback list when it can't say. */
export function listTimeZones(): readonly string[] {
  try {
    const zones = (Intl as unknown as IntlWithZones).supportedValuesOf?.('timeZone');
    if (Array.isArray(zones) && zones.length > 0) return [...zones].sort();
  } catch {
    // Some runtimes throw rather than return undefined; the fallback covers both.
  }
  return FALLBACK_TIME_ZONES;
}

export interface TimeZoneGroup {
  /** `Europe`, `America`, … — the part before the first slash. */
  readonly region: string;
  readonly zones: readonly string[];
}

/** The zones grouped by region, for a picker with section headings. */
export function groupTimeZones(zones: readonly string[] = listTimeZones()): TimeZoneGroup[] {
  const groups = new Map<string, string[]>();
  for (const zone of zones) {
    const region = zone.includes('/') ? (zone.split('/')[0] ?? 'Other') : 'Other';
    const existing = groups.get(region);
    if (existing) existing.push(zone);
    else groups.set(region, [zone]);
  }
  return [...groups.entries()]
    .map(([region, list]) => ({ region, zones: list }))
    .sort((a, b) => a.region.localeCompare(b.region));
}

/** `America/Argentina/Buenos_Aires` → `Buenos Aires` — the part a picker shows under its region. */
export function timeZoneLabel(zone: string): string {
  const parts = zone.split('/');
  return (parts[parts.length - 1] ?? zone).replace(/_/g, ' ');
}

/** The country or territory a zone represents, when tzdb has one for it. */
export function timeZoneCountry(zone: string, locale: string = DEFAULT_LOCALE): string | undefined {
  const countryCode = TIME_ZONE_COUNTRIES.get(zone);
  return countryCode === undefined
    ? runtimeTimeZoneCountry(zone)
    : countryDisplayName(countryCode, locale);
}

/**
 * A picker-facing place name: `New York, United States`, rather than an IANA identifier.
 *
 * The city half comes out of the IANA identifier and stays as it is spelled there — translating
 * `Europe/Cologne` to `Köln` would need a CLDR city table this package deliberately doesn't ship.
 */
export function timeZonePlaceLabel(zone: string, locale: string = DEFAULT_LOCALE): string {
  const city = timeZoneLabel(zone);
  const country = timeZoneCountry(zone, locale);
  return country === undefined ? city : `${city}, ${country}`;
}

/** Country aliases people commonly type instead of the formal display name. */
const COUNTRY_SEARCH_ALIASES: ReadonlyMap<string, string> = new Map([
  ['AE', 'UAE'],
  ['GB', 'UK Britain England'],
  ['US', 'US USA America'],
  ['CZ', 'Czechia'],
  ['NL', 'Holland'],
  ['NZ', 'NZ'],
  ['KR', 'Korea'],
  ['TR', 'Turkey'],
]);

/**
 * Text used by autocomplete matching; includes the city, country, and IANA spelling.
 *
 * A localized country name is *added* to the English one rather than replacing it. Someone on a
 * German page still types "Germany" as often as "Deutschland" — the country names in a calendar
 * invitation, an airline itinerary, or a colleague's message are frequently English whatever the
 * browser is set to — and a picker that stopped answering the English spelling would be a
 * regression sold as a feature.
 *
 * The two are compared by the name they produce rather than by the tag asked for, because most
 * regional variants name most countries identically: `en-GB` differs from `en` for 9 of the 280
 * regions Intl knows, `es-MX` from `es` for 13. Comparing tags would put "Germany Germany" in the
 * search text of every zone for every visitor who picked a variant of the default language.
 */
export function timeZoneSearchText(zone: string, locale: string = DEFAULT_LOCALE): string {
  const countryCode = TIME_ZONE_COUNTRIES.get(zone);
  // Through `timeZoneCountry`, not the map directly, so this picks up the runtime fallback that
  // `timeZonePlaceLabel` already uses. Reading the map here instead left every zone outside the
  // compact map — 155 of the 428 this runtime lists — showing a country in its label that its
  // own search text did not contain, so `Europe/London` displayed "London, United Kingdom" and
  // then found nothing when that exact label was typed back into the box.
  const country = timeZoneCountry(zone, DEFAULT_LOCALE);
  const localized = timeZoneCountry(zone, locale);
  const localizedCountry = localized === country ? undefined : localized;
  const countryAliases =
    countryCode === undefined ? undefined : COUNTRY_SEARCH_ALIASES.get(countryCode);
  return [
    timeZoneLabel(zone),
    country,
    localizedCountry,
    countryAliases,
    // Keep the identifier searchable for people who already know IANA names. Replacing the
    // separators makes `new york` match America/New_York just like the friendly city label does.
    zone.replace(/[/_]+/g, ' '),
  ]
    .filter((part): part is string => part !== undefined)
    .join(' ');
}

/**
 * Case, accents and punctuation removed, so `Köln` and `koln` are the same query.
 *
 * Letters and digits are kept in every script rather than only in ASCII: a locale's country name
 * can be `日本` or `Россия`, and stripping to `[a-z0-9]` would reduce both the label and the query
 * someone typed to an empty string — the picker would then match everything instead of that
 * country, which looks far more like a bug than a missing translation does.
 */
function normalizedSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

interface TimeZoneSearchIndex {
  readonly city: string;
  readonly search: string;
  /** Both spellings, so typing the country in English on a translated page ranks as a hit. */
  readonly countries: readonly string[];
}

/**
 * The normalized text a zone is matched against, built once per zone and locale.
 *
 * Everything in here is derived from values that cannot change while the page is open — a zone's
 * city, its country and its identifier — so there is nothing to invalidate. Recomputing it per
 * keystroke is what made the picker expensive: a query scores every one of the ~430 zones, and
 * each zone outside the compact country map builds an `Intl.DateTimeFormat` and runs
 * `formatToParts` to find its country, twice.
 */
const SEARCH_INDEX = new Map<string, TimeZoneSearchIndex>();

function timeZoneSearchIndex(zone: string, locale: string): TimeZoneSearchIndex {
  // `\u0000` cannot occur in a zone id or a BCP-47 tag, so no pair of them can collide on a key.
  const key = `${locale}\u0000${zone}`;
  const cached = SEARCH_INDEX.get(key);
  if (cached !== undefined) return cached;
  const entry: TimeZoneSearchIndex = {
    city: normalizedSearch(timeZoneLabel(zone)),
    search: normalizedSearch(timeZoneSearchText(zone, locale)),
    countries: [
      ...new Set(
        [
          normalizedSearch(timeZoneCountry(zone, locale) ?? ''),
          normalizedSearch(timeZoneCountry(zone) ?? ''),
        ].filter((country) => country !== ''),
      ),
    ],
  };
  SEARCH_INDEX.set(key, entry);
  return entry;
}

function timeZoneSearchScore(zone: string, query: string, locale: string): number {
  const { city, search, countries } = timeZoneSearchIndex(zone, locale);
  if (city === query) return 0;
  if (countries.includes(query)) return 1;
  if (city.startsWith(query)) return 2;
  if (countries.some((country) => country.startsWith(query))) return 3;
  if (search.startsWith(query)) return 4;
  return 5;
}

/**
 * Finds zones for a human query, in relevance order. Every query word must occur somewhere in
 * the city, country, alias, or IANA identifier, so `united states`, `new york`, and
 * `america/new` all work without making the caller know the storage format.
 */
export function filterTimeZones(
  zones: readonly string[],
  query: string,
  locale: string = DEFAULT_LOCALE,
): readonly string[] {
  const normalizedQuery = normalizedSearch(query);
  if (normalizedQuery === '') return zones;
  const words = normalizedQuery.split(' ');
  return zones
    .map((zone, index) => ({
      zone,
      index,
      search: timeZoneSearchIndex(zone, locale).search,
      score: timeZoneSearchScore(zone, normalizedQuery, locale),
    }))
    .filter(({ search }) => words.every((word) => search.includes(word)))
    .sort((a, b) => a.score - b.score || a.index - b.index)
    .map(({ zone }) => zone);
}

/** Resolves a selected friendly label (or an IANA ID) back to the value written into the event. */
export function matchTimeZone(
  value: string,
  zones: readonly string[],
  locale: string = DEFAULT_LOCALE,
): string | undefined {
  const query = normalizedSearch(value);
  if (query === '') return undefined;

  // The English label is accepted alongside the localized one for the same reason the search text
  // carries both — and because a label pasted from elsewhere, or typed before the host changed its
  // locale, must still resolve to the zone it names rather than silently to nothing.
  const exact = zones.find(
    (zone) =>
      normalizedSearch(zone) === query ||
      normalizedSearch(timeZonePlaceLabel(zone, locale)) === query ||
      normalizedSearch(timeZonePlaceLabel(zone)) === query,
  );
  if (exact !== undefined) return exact;

  // A city can be accepted without the country only when it identifies one zone. Never guess
  // between the several New York-like entries a country or broad region may return.
  const cityMatches = zones.filter((zone) => normalizedSearch(timeZoneLabel(zone)) === query);
  return cityMatches.length === 1 ? cityMatches[0] : undefined;
}

/** The UTC offset at a local event time, suitable for showing beside a time picker. */
export function timeZoneOffsetLabel(zone: string, dateTime: string): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dateTime)) return undefined;
  const instant = new Date(`${dateTime}:00Z`);
  if (Number.isNaN(instant.getTime())) return undefined;
  try {
    // 'en-US' is not a display choice and does not follow the caller's locale: this string is
    // parsed by the regex below and re-emitted as `GMT+02:00`, which reads the same everywhere.
    const part = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'longOffset',
    })
      .formatToParts(instant)
      .find((item) => item.type === 'timeZoneName')?.value;
    if (part === undefined || part === 'GMT') return 'GMT+00:00';
    const match = /^GMT([+-])(\d{1,2})(?::(\d{2}))?$/.exec(part);
    if (match === null) return part;
    return `GMT${match[1]}${match[2]!.padStart(2, '0')}:${match[3] ?? '00'}`;
  } catch {
    return undefined;
  }
}

/** A numeric offset for ordering zones, or `undefined` when the runtime cannot resolve it. */
function timeZoneOffsetMinutes(zone: string, dateTime: string): number | undefined {
  const offset = timeZoneOffsetLabel(zone, dateTime);
  if (offset === undefined) return undefined;
  const match = /^GMT([+-])(\d{2}):(\d{2})$/.exec(offset);
  if (match === null) return undefined;
  const minutes = Number(match[2]) * 60 + Number(match[3]);
  return match[1] === '-' ? -minutes : minutes;
}

/** A picker label with the event-time offset and a friendly city/country name. */
export function timeZoneOptionLabel(
  zone: string,
  dateTime: string,
  locale: string = DEFAULT_LOCALE,
): string {
  const offset = timeZoneOffsetLabel(zone, dateTime);
  return offset === undefined
    ? timeZonePlaceLabel(zone, locale)
    : `${offset} — ${timeZonePlaceLabel(zone, locale)}`;
}

/** Sorts zones by their offset at the event time, then by their displayed city name. */
export function sortTimeZonesByOffset(
  zones: readonly string[],
  dateTime: string,
): readonly string[] {
  return zones
    .map((zone) => ({
      zone,
      city: timeZoneLabel(zone),
      offset: timeZoneOffsetMinutes(zone, dateTime),
    }))
    .sort((a, b) => {
      if (a.offset === undefined && b.offset === undefined) {
        return a.city.localeCompare(b.city);
      }
      if (a.offset === undefined) return 1;
      if (b.offset === undefined) return -1;
      return a.offset - b.offset || a.city.localeCompare(b.city);
    })
    .map(({ zone }) => zone);
}

/**
 * The list with `zone` guaranteed present. A draft can arrive carrying a zone this runtime
 * doesn't enumerate (a host prefill, an older list); dropping it from the picker would silently
 * change the event when the user next touched the field.
 */
export function withTimeZone(
  zone: string | undefined,
  zones: readonly string[] = listTimeZones(),
): readonly string[] {
  if (zone === undefined || zone === '' || zones.includes(zone)) return zones;
  return [...zones, zone].sort();
}

/** The device's zone, or `undefined` where the runtime won't say. */
export function localTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}
